import { useEffect, useState } from "react";
import { Bar, Doughnut, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import {
  Box,
  Button,
  Grid,
  TextField,
  Modal,
  Typography,
  Autocomplete,
  CircularProgress,
  FormControlLabel,
  Switch,
} from "@mui/material";
import axios from "axios";
import { coopsData } from "../../utils/coopsData";

// Register necessary Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

type Props = {};

const FederatedReports = (props: Props) => {
  const BASE_URL = process.env.REACT_APP_API_KEY;
  const [chartData, setChartData] = useState<any[]>([]);
  const [userRole, setUserRole] = useState(localStorage.getItem("role") || "");
  const [coopData, setCoopData] = useState({ systemList: [] });
  const today = new Date().toISOString().split("T")[0];
  const last7Days = new Date();
  last7Days.setDate(last7Days.getDate() - 7);
  const last7DaysFormatted = last7Days.toISOString().split("T")[0];
  const [fromDate, setFromDate] = useState(last7DaysFormatted);
  const [toDate, setToDate] = useState(today);
  const [isEditable, setIsEditable] = useState(true);
  const [selectedCoopKeys, setSelectedCoopKeys] = useState<string[]>(
    coopData?.systemList?.map((coop: any) => coop?.systemNumber)
  );
  const [userSystemNum, setUserSystemNum] = useState("");
  const [all, setAll] = useState(true);
  const [sysNum, setSysNum] = useState(["15063"]);
  const [loggedInSysNum, setLoggedInSysNum] = useState("");
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  const [disable, setDisable] = useState(false);

  const [pieChartData, setPieChartData] = useState({
    labels: [],
    datasets: [
      {
        label: "My Pie Chart",
        data: [300, 50, 100],
        backgroundColor: [
          "rgba(255, 99, 132, 0.2)",
          "rgba(54, 162, 235, 0.2)",
          "rgba(255, 206, 86, 0.2)",
        ],
        borderColor: [
          "rgba(255, 99, 132, 1)",
          "rgba(54, 162, 235, 1)",
          "rgba(255, 206, 86, 1)",
        ],
        borderWidth: 1,
      },
    ],
  });

  const [barChartData, setBarChartData] = useState({
    labels: [],
    datasets: [
      {
        label: "Submissions Count",
        data: [],
        backgroundColor: "rgba(75, 192, 192, 0.2)",
        borderColor: "rgba(75, 192, 192, 1)",
        borderWidth: 1,
      },
    ],
  });

  const [jobBreifyingDatachart, setJobBreifyingDatachart] = useState({
    labels: [],
    datasets: [
      {
        label: "Job Briefing Job Type Count",
        data: [],
        backgroundColor: ["#FF5733", // Standard Color 1
          "#33FF57", // Standard Color 2
          "#3357FF", // Standard Color 3
          "#F1C40F", // Standard Color 4
          "#8E44AD", // Standard Color 5
          "#E74C3C", // Standard Color 6
          "#2ECC71", // Standard Color 7
          "#3498DB", // Standard Color 8
          "#9B59B6", // Standard Color 9
          "#F39C12"],
        borderColor: "rgba(255, 255, 255, 1)",
        borderWidth: 1,
      },
    ],
  });

  const [HazardDatachart, setHazardDatachart] = useState({
    labels: [],
    datasets: [
      {
        label: "Hazard Recognition Hazard Type Count",
        data: [],
        backgroundColor: [
          "#FF6384", // Red
          "#36A2EB", // Blue
          "#FFCE56", // Yellow
          "#4BC0C0", // Teal
          "#9966FF", // Purple
          "#FF9F40", // Orange
          "#8B0000", // Dark Red
          "#00CED1", // Dark Turquoise
          "#ADFF2F", // Green Yellow
          "#DC143C", // Crimson
          "#20B2AA", // Light Sea Green
          "#9370DB", // Medium Purple
          "#3CB371", // Medium Sea Green
          "#FFD700", // Gold
          "#40E0D0"  // Turquoise
        ],
        borderColor: "rgba(255, 255, 255, 1)",
        borderWidth: 1,
      },
    ],
  });

  const [vehicleDatachart, setVehicleDatachart] = useState({
    labels: [],
    datasets: [
      {
        label: "Vehicle/Trailer Inspection Vehicle Count",
        data: [],
        backgroundColor: [
          "#FF5733", "#33FF57", "#3357FF", "#FF33A1", "#A133FF",
          "#33FFF3", "#F3FF33", "#FF8C33", "#8C33FF", "#33FF8C",
          "#FF3333", "#33FF33", "#3333FF", "#FF33FF", "#33FFFF",
          "#FFFF33", "#FFB533", "#B533FF", "#33FFB5", "#FF3357",
          "#57FF33", "#5733FF", "#FF5733", "#33A1FF", "#A1FF33",
          "#FF33A1", "#33FFA1", "#A133FF", "#FFA133", "#33A133",
          "#8CFF33", "#338CFF", "#FF338C", "#8C33FF", "#33FF8C",
          "#FF6633", "#66FF33", "#3366FF", "#FF3366", "#66FFCC",
          "#CCFF66", "#FFCC66", "#66CCFF", "#CC66FF", "#66FF99",
          "#FF9966", "#9966FF", "#66FF66", "#6666FF", "#FF6666"
        ],
        borderColor: "rgba(255, 255, 255, 1)",
        borderWidth: 1,
      },
    ],
  });

  const [jobsiteDatachart, setJobsiteDatachart] = useState({
    labels: [],
    datasets: [
      {
        label: "Job Site Observation Count",
        data: [],
        backgroundColor: [
          "#FF5733", "#33FF57", "#3357FF", "#FF33A1", "#A133FF",
          "#33FFF3", "#F3FF33", "#FF8C33", "#8C33FF", "#33FF8C",
          "#FF3333", "#33FF33", "#3333FF", "#FF33FF", "#33FFFF",
          "#FFFF33", "#FFB533", "#B533FF", "#33FFB5", "#FF3357",
          "#57FF33", "#5733FF", "#FF5733", "#33A1FF", "#A1FF33",
          "#FF33A1", "#33FFA1", "#A133FF", "#FFA133", "#33A133",
          "#8CFF33", "#338CFF", "#FF338C", "#8C33FF", "#33FF8C",
          "#FF6633", "#66FF33", "#3366FF", "#FF3366", "#66FFCC",
          "#CCFF66", "#FFCC66", "#66CCFF", "#CC66FF", "#66FF99",
          "#FF9966", "#9966FF", "#66FF66", "#6666FF", "#FF6666"
        ],
        borderColor: "rgba(255, 255, 255, 1)",
        borderWidth: 1,
      },
    ],
  });

  // useEffect(() => {
  //     const systemNumber = localStorage.getItem("systemNumber") || "";

  //     if (systemNumber === "15063") {

  //     } else {
  //       setSysNum((prevSysNum) => {
  //         // Check if systemNumber already exists in sysNum
  //         if (!prevSysNum.includes(systemNumber)) {
  //           return [...prevSysNum, systemNumber];
  //         }
  //         return prevSysNum; // If it exists, return the previous state unchanged
  //       });
  //       setAll(false);
  //     }
  //     setLoggedInSysNum(systemNumber);
  //   }, []);

  const handleAllCheckboxChange = () => {
    if (selectedCoopKeys.length === coopData.systemList.length) {
      // When deselecting all, keep the userSystemNum selected
      const newUserSystemNumSelected = userSystemNum ? [userSystemNum] : [];
      setSelectedCoopKeys(newUserSystemNumSelected);
      setAll(false);
    } else {
      // When selecting all, include all coop keys plus the userSystemNum if not already included
      const allCoopKeys = coopData.systemList.map(
        (coop: any) => coop.systemNumber
      );
      if (userSystemNum && !allCoopKeys.includes(userSystemNum)) {
        allCoopKeys.push(userSystemNum);
      }
      setSelectedCoopKeys(allCoopKeys);
      setAll(true);
    }
  };

  // Random color generator function
  const getRandomColor = () => {
    const letters = "0123456789ABCDEF";
    let color = "#";
    for (let i = 0; i < 6; i++) {
      color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
  };

  const fetchCoops = async () => {
    const systemNumber = localStorage.getItem("systemNumber") || "";

    if (systemNumber === "15063") {
      const coopData = await coopsData();

      setCoopData(coopData);

      // Automatically select all coop keys when data is fetched

      const allCoopKeys = coopData.systemList.map(
        (coop: any) => coop.systemNumber
      );

      setSelectedCoopKeys(allCoopKeys);

      await fetchChart(coopData); // <-- Pass coopData as param
    } else {
      await fetchChart([]);
    }
  };

  //   console.log("coopData",coopData)

  type System = {
    systemNumber: string;
    systemName: string;
    // other fields?
  };

  type DataItem = {
    _id: string;
    sysName?: string;
    // other fields?
  };

  const fetchChart = async (coopsData: any) => {
    const SystemNumber = localStorage.getItem("systemNumber");
    const token = localStorage.getItem("authtoken") || "";

    if (userRole === "superadmin") {
      //await fetchCoops();
      // console.log(coopsData)
      // console.log('getCoopsListReports', fromDate, toDate)

      try {
        const config = {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        };

        const response = await axios.get(
          `${BASE_URL}/document/getFederatedAdminReports?fromDate=${fromDate}&toDate=${toDate}`,
          config
        );

        const backendFiles = response.data.getchartData;
        if (backendFiles && backendFiles[0]) {
          const updatedPieChartData = {
            labels: backendFiles[0].labels || [],
            datasets: [
              {
                label: "Submissions Count",
                data: backendFiles[0].count || [],
                backgroundColor: "rgba(75, 192, 192, 0.2)",
                borderColor: "rgba(75, 192, 192, 1)",
                borderWidth: 1,
              },
            ],
          };

          setBarChartData(updatedPieChartData);
        } else {
          console.error("Invalid data structure returned from backend.");
        }

        const dataFiles = response.data.getsysNumchart;
        const result = dataFiles.filter((item: DataItem) => {
          const match = (coopsData.systemList as System[]).find(
            (sys) => sys.systemNumber === item._id
          );
          if (match) {
            item.sysName = match.systemName;
            return true;
          }
          return false;
        });
        //console.log(result)
        setChartData(result);
        // setTotalPages(result.length);
        setTotalPages(Math.ceil(result.length / 10));
      } catch (error) {
        console.error("Error fetching documents:", error);
      }
    } else {
      try {
        const config = {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        };

        const response = await axios.get(
          `${BASE_URL}/document/getCoopsListReports/${SystemNumber}?fromDate=${fromDate}&toDate=${toDate}`,
          config
        );

        const JobBreifyingDatachart = response.data.getJobBreifyingDatachart;
        if (JobBreifyingDatachart && JobBreifyingDatachart[0]) {
          // Create random colors for each count
          const randomColors = JobBreifyingDatachart[0].count.map(() =>
            getRandomColor()
          );
          // console.log("JobBreifyingDatachart[0].labels.sort() ",JobBreifyingDatachart[0].labels.sort())
          const updatedJobBreifyingDatachart = {
            labels: JobBreifyingDatachart[0].labels || [],
            datasets: [
              {
                label: "Job Briefing Job Type Count",
                data: JobBreifyingDatachart[0].count || [],
                backgroundColor: ["#FF5733", // Standard Color 1
                  "#33FF57", // Standard Color 2
                  "#3357FF", // Standard Color 3
                  "#F1C40F", // Standard Color 4
                  "#8E44AD", // Standard Color 5
                  "#E74C3C", // Standard Color 6
                  "#2ECC71", // Standard Color 7
                  "#3498DB", // Standard Color 8
                  "#9B59B6", // Standard Color 9
                  "#F39C12"], // Use random colors for each data point
                borderColor: "rgba(255, 255, 255, 1)",
                borderWidth: 1,
              },
            ],
          };

          setJobBreifyingDatachart(updatedJobBreifyingDatachart);
        } else {
          console.error("Invalid data structure returned from backend.");
        }

        const JobSiteDatachart = response.data.getJobSiteDatachart;

        if (JobSiteDatachart && JobSiteDatachart[0]) {
          // Create random colors for each count
          const randomColors = [
            "#FF5733", "#33FF57", "#3357FF", "#FF33A1", "#A133FF",
            "#33FFF3", "#F3FF33", "#FF8C33", "#8C33FF", "#33FF8C",
            "#FF3333", "#33FF33", "#3333FF", "#FF33FF", "#33FFFF",
            "#FFFF33", "#FFB533", "#B533FF", "#33FFB5", "#FF3357",
            "#57FF33", "#5733FF", "#FF5733", "#33A1FF", "#A1FF33",
            "#FF33A1", "#33FFA1", "#A133FF", "#FFA133", "#33A133",
            "#8CFF33", "#338CFF", "#FF338C", "#8C33FF", "#33FF8C",
            "#FF6633", "#66FF33", "#3366FF", "#FF3366", "#66FFCC",
            "#CCFF66", "#FFCC66", "#66CCFF", "#CC66FF", "#66FF99",
            "#FF9966", "#9966FF", "#66FF66", "#6666FF", "#FF6666"
          ]

          const updatedJobSiteDatachart = {
            labels: JobSiteDatachart[0].labels || [],
            datasets: [
              {
                label: "Job Site Observation Count",
                data: JobSiteDatachart[0].count || [],
                backgroundColor: randomColors, // Use random colors for each data point
                borderColor: "rgba(255, 255, 255, 1)",
                borderWidth: 1,
              },
            ],
          };

          setJobsiteDatachart(updatedJobSiteDatachart);
        } else {
          console.error("Invalid data structure returned from backend.");
        }

        const vehicleDatachart = response.data.getvehicleDatachart;

        if (vehicleDatachart && vehicleDatachart[0]) {
          // Create random colors for each count
          const randomColors = [
            "#FF5733", "#33FF57", "#3357FF", "#FF33A1", "#A133FF",
            "#33FFF3", "#F3FF33", "#FF8C33", "#8C33FF", "#33FF8C",
            "#FF3333", "#33FF33", "#3333FF", "#FF33FF", "#33FFFF",
            "#FFFF33", "#FFB533", "#B533FF", "#33FFB5", "#FF3357",
            "#57FF33", "#5733FF", "#FF5733", "#33A1FF", "#A1FF33",
            "#FF33A1", "#33FFA1", "#A133FF", "#FFA133", "#33A133",
            "#8CFF33", "#338CFF", "#FF338C", "#8C33FF", "#33FF8C",
            "#FF6633", "#66FF33", "#3366FF", "#FF3366", "#66FFCC",
            "#CCFF66", "#FFCC66", "#66CCFF", "#CC66FF", "#66FF99",
            "#FF9966", "#9966FF", "#66FF66", "#6666FF", "#FF6666"
          ];


          const updatedJobSiteDatachart = {
            labels: vehicleDatachart[0].labels || [],
            datasets: [
              {
                label: "Vehicle/Trailer Inspection Vehicle Count",
                data: vehicleDatachart[0].count || [],
                backgroundColor: randomColors, // Use random colors for each data point
                borderColor: "rgba(255, 255, 255, 1)",
                borderWidth: 1,
              },
            ],
          };

          setVehicleDatachart(updatedJobSiteDatachart);
        } else {
          console.error("Invalid data structure returned from backend.");
        }

        const HazardDatachart = response.data.getHazardDatachart;

        if (HazardDatachart && HazardDatachart[0]) {
          // Create random colors for each count
          const randomColors = [
            "#FF6384", // Red
            "#36A2EB", // Blue
            "#FFCE56", // Yellow
            "#4BC0C0", // Teal
            "#9966FF", // Purple
            "#FF9F40", // Orange
            "#8B0000", // Dark Red
            "#00CED1", // Dark Turquoise
            "#ADFF2F", // Green Yellow
            "#DC143C", // Crimson
            "#20B2AA", // Light Sea Green
            "#9370DB", // Medium Purple
            "#3CB371", // Medium Sea Green
            "#FFD700", // Gold
            "#40E0D0"  // Turquoise
          ]

          const updatedJobSiteDatachart = {
            labels: HazardDatachart[0].labels || [],
            datasets: [
              {
                label: "Hazard Recognition Hazard Count",
                data: HazardDatachart[0].count || [],
                backgroundColor: randomColors,
                borderColor: "rgba(255, 255, 255, 1)",
                borderWidth: 1,
              },
            ],
          };

          setHazardDatachart(updatedJobSiteDatachart);
        } else {
          console.error("Invalid data structure returned from backend.");
        }
      } catch (error) {
        console.error("Error fetching documents:", error);
      }
    }
  };

  useEffect(() => {
    const fetchAll = async () => {
      await fetchCoops();
      // await fetchChart(); // Will run after coops are set
    };
    fetchAll();
  }, []);

  const handleItemsPerPageChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setItemsPerPage(Number(e.target.value));
    setPage(1); // reset to first page
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const renderPaginationRange = () => {
    const totalPages = Math.ceil(chartData.length / itemsPerPage);
    const pageNumbers = [];
  
    const maxPageButtons = 5;
    const halfRange = Math.floor(maxPageButtons / 2);
  
    let startPage = Math.max(1, page - halfRange);
    let endPage = Math.min(totalPages, startPage + maxPageButtons - 1);
  
    // Adjust startPage if we're near the end
    if (endPage - startPage < maxPageButtons - 1) {
      startPage = Math.max(1, endPage - maxPageButtons + 1);
    }
  
    // First page button (if not in range)
    if (startPage > 1) {
      pageNumbers.push(
        <button key={1} onClick={() => handlePageChange(1)} disabled={disable}>
          1
        </button>
      );
      if (startPage > 2) {
        pageNumbers.push(<span key="start-ellipsis">...</span>);
      }
    }
  
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          disabled={page === i || disable}
          className={page === i ? "active-page" : ""}
        >
          {i}
        </button>
      );
    }
  
    // Last page button (if not in range)
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pageNumbers.push(<span key="end-ellipsis">...</span>);
      }
      pageNumbers.push(
        <button
          key={totalPages}
          onClick={() => handlePageChange(totalPages)}
          disabled={disable}
        >
          {totalPages}
        </button>
      );
    }
  
    return pageNumbers;
  };

  const handlePreviousPage = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  const handleChartSearch = () => {
    fetchCoops();
  }

  const handleNextPage = () => {
    const totalPages = Math.ceil(chartData.length / itemsPerPage);
    if (page < Math.min(10, totalPages)) {
      setPage(page + 1);
    }
  };

  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = chartData.slice(startIndex, endIndex);
  //   const totalPages = Math.ceil(chartData.length / itemsPerPage);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "30px",
      }}
    >
      {userRole === "superadmin" ? (
        <>
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "flex-end",
              gap: "20px",
              marginBottom: "15px",
              flexWrap: "wrap",
            }}
          >
            <TextField
              id="fromDate"
              label="From Date"
              type="date"
              required
              value={fromDate}
              onChange={(e) => {
                setFromDate(e.target.value);
                if (new Date(e.target.value) > new Date(toDate)) {
                  setToDate(""); // Reset toDate if fromDate is greater
                }
              }}
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 1 }}
              InputProps={{
                readOnly: !isEditable,
                inputProps: {
                  max: toDate || today,
                },
              }}
            />

            <TextField
              id="toDate"
              label="To Date"
              type="date"
              value={toDate}
              onChange={(e) => {
                setToDate(e.target.value);
                if (new Date(e.target.value) < new Date(fromDate)) {
                  setFromDate(""); // Reset fromDate if toDate is less
                }
              }}
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 1 }}
              required
              InputProps={{
                readOnly: !isEditable,
                inputProps: {
                  min: fromDate,
                  max: today,
                },
              }}
            />

            <button
              className="blue"
              onClick={handleChartSearch}
              disabled={disable}
            >
              Search
            </button>

          </div>

          <div
            style={{
              padding: "20px",
              maxWidth: "600px",
              width: "100%",
              textAlign: "center",
            }}
          >
            <h3>Form Submissions Chart</h3>
            <p style={{ fontWeight: "bold" }}>
              Total Submissions: {barChartData.datasets[0].data.reduce((a: number, b: number) => a + b, 0)}
            </p>
            <Bar data={barChartData} />
          </div>

          {paginatedData.length > 0 && (
            <div style={{ overflowX: "auto", textAlign: "center" }}>
              <h3>System Submissions</h3>
              <p style={{ fontWeight: "bold" }}>
                Number of Coops Submitted: {chartData.length}
              </p>
              <table
                cellPadding="10"
                cellSpacing="0"
                style={{ margin: "0 auto", borderCollapse: "collapse" }}
              >
                <thead>
                  <tr>
                    <th>System Number</th>
                    <th>System Name</th>
                    <th>Submissions Count</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedData.map((item: any) => (
                    <tr key={item._id}>
                      <td>{item._id}</td>
                      <td>{item.sysName}</td>
                      <td>{item.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Items Per Page Dropdown */}
              <div style={{ marginTop: "15px" }}>
                <label>
                  Items per page: &nbsp;
                  <select
                    value={itemsPerPage}
                    onChange={handleItemsPerPageChange}
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </label>
              </div>

              {/* Pagination Buttons */}
              <div
                style={{
                  marginTop: "15px",
                  display: "flex",
                  gap: "10px",
                  justifyContent: "center",
                }}
              >
                <button
                  onClick={handlePreviousPage}
                  disabled={page === 1 || disable}
                >
                  &lt;{" "}
                </button>
                {renderPaginationRange()}
                <button
                  onClick={handleNextPage}
                  disabled={page >= totalPages || disable}
                >
                  {" "}
                  &gt;
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "flex-end",
              gap: "20px",
              marginBottom: "15px",
              flexWrap: "wrap",
            }}
          >
            <TextField
              id="fromDate"
              label="From Date"
              type="date"
              required
              value={fromDate}
              onChange={(e) => {
                setFromDate(e.target.value);
                if (new Date(e.target.value) > new Date(toDate)) {
                  setToDate(""); // Reset toDate if fromDate is greater
                }
              }}
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 1 }}
              InputProps={{
                readOnly: !isEditable,
                inputProps: {
                  max: toDate || today,
                },
              }}
            />

            <TextField
              id="toDate"
              label="To Date"
              type="date"
              value={toDate}
              onChange={(e) => {
                setToDate(e.target.value);
                if (new Date(e.target.value) < new Date(fromDate)) {
                  setFromDate(""); // Reset fromDate if toDate is less
                }
              }}
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 1 }}
              required
              InputProps={{
                readOnly: !isEditable,
                inputProps: {
                  min: fromDate,
                  max: today,
                },
              }}
            />

            <button
              className="blue"
              onClick={handleChartSearch}
              disabled={disable}
            >
              Search
            </button>

          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "30px",
            }}
          >
            {/* First Row */}
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "30px",
                flexWrap: "wrap",
              }}
            >
              {jobBreifyingDatachart.labels.length > 0 && (
                <div
                  style={{
                    padding: "20px",
                    width: "550px",
                    textAlign: "center",
                  }}
                >
                  <h3>Job Briefing Job Type Count</h3>
                  <Pie data={jobBreifyingDatachart} />
                </div>
              )}
              {HazardDatachart.labels.length > 0 && (
                <div
                  style={{
                    padding: "20px",
                    width: "550px",
                    textAlign: "center",
                  }}
                >
                  <h3>Hazard Recognition Hazard Count</h3>
                  <Bar data={HazardDatachart} />
                </div>
              )}
            </div>

            {/* Second Row */}
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "30px",
                flexWrap: "wrap",
              }}
            >
              {vehicleDatachart.labels.length > 0 && (
                <div
                  style={{
                    padding: "20px",
                    width: "550px",
                    textAlign: "center",
                  }}
                >
                  <h3>Vehicle/Trailer Inspection Vehicle Count</h3>
                  <Doughnut data={vehicleDatachart} />
                </div>
              )}

              {jobsiteDatachart.labels.length > 0 && (
                <div
                  style={{
                    padding: "20px",
                    width: "550px",
                    textAlign: "center",
                  }}
                >
                  <h3>Job Site Observation Count</h3>
                  <Doughnut data={jobsiteDatachart} />
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default FederatedReports;
